/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useState, useEffect} from "react";
import * as yup from "yup";
import toast from "react-hot-toast";
import {ErrorMessage, Form, Formik} from "formik";
import axios from "axios";
import {useNavigate, useLocation} from "react-router-dom";
import {PasswordValidationStatus} from "./PasswordValidationStatus";
import {Button, EyeIcon, Input, InputWrapper} from "./Login";
import {BsEyeFill} from "react-icons/bs";

const initialValues = {
  password: "",
  confirm_password: "",
};

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?\":{}|<>]/,
      "Password must contain at least one special character"
    ),
  confirm_password: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password")], "Passwords must match"),
});

export default function CreateNewPasswordForm({
  setStep,
  email,
}: {
  setStep: React.Dispatch<React.SetStateAction<string>>;
  email: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const Baseurl = process.env.REACT_APP_BASEURL;
  const [isLoading, setIsLoading] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const handleToggle = () => setPasswordShown(!passwordShown);

  // Extract token from query params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid or missing reset token. Please log in again.");
      navigate("/login");
    }
  }, [token, navigate]);

  const onSubmit = async (values: typeof initialValues) => {
    setIsLoading(true);
    try {
      await axios.post(
        `${Baseurl}/api/admin/auth/reset-password`,
        {
          token,
          password: values.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Password changed successfully!");
      setStep("success");
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({values, setFieldValue}) => (
          <Form autoComplete="off">
            <div className="grid grid-cols-1 gap-4 w-full mb-4">
              {/* Password */}
              <InputWrapper>
                <Input
                  type={passwordShown ? "text" : "password"}
                  name="password"
                  value={values.password}
                  onChange={(e) => setFieldValue("password", e.target.value)}
                  placeholder="Password"
                  required
                />
                <EyeIcon onClick={handleToggle}>
                  <BsEyeFill />
                </EyeIcon>
              </InputWrapper>

              {values.password && (
                <PasswordValidationStatus password={values.password} />
              )}

              {/* Confirm Password */}
              <InputWrapper>
                <Input
                  type={passwordShown ? "text" : "password"}
                  name="confirm_password"
                  value={values.confirm_password}
                  onChange={(e) =>
                    setFieldValue("confirm_password", e.target.value)
                  }
                  placeholder="Confirm Password"
                  required
                />
                <EyeIcon onClick={handleToggle}>
                  <BsEyeFill />
                </EyeIcon>
              </InputWrapper>

              <ErrorMessage name="confirm_password">
                {(msg) => (
                  <div className="flex items-center gap-1 mt-1">
                    <p className="text-xs font-semibold text-[#DF1C41]">
                      {msg}
                    </p>
                  </div>
                )}
              </ErrorMessage>
            </div>

            <Button
              className="!w-full mt-8 !bg-black !text-white"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Create Password"}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}
